/**
 * Utilities for Vuetify dropdown menus (`v-menu`, `v-combobox`, etc.) inside
 * scrollable dialogs and nested overlays.
 *
 * Problem: attached menus often clip, sit under dialog layers, or keep stale
 * inline `top`/`left` after the activator moves. Some fields (tags, user
 * select, lb-dropdown) teleport or re-open menus and need width, z-index, and
 * list height synced to the activator.
 *
 * Exports:
 * - `syncAttachedMenuLayout` — match menu width to activator, raise z-index,
 *   cap list scroll height (search + footer aware).
 * - `clearMenuPositionOverrides` — remove manual position styles so Vuetify
 *   can re-anchor.
 * - `positionMenuNearActivator` — pin menu above or below the field from available space.
 * - `positionMenuAboveActivator` — pin menu above the field (forced upward).
 * - `updateOpenMenuDimensions` — trigger Vuetify `updateDimensions()`.
 *
 * Used by Add Task dialog fields, TagInputs, Dropdown, UserChipMultiSelect, etc.
 */
export const DIALOG_MENU_Z_INDEX = 10000;

const DEFAULT_MAX_MENU_HEIGHT = 320;
const MENU_GAP = 4;
const MIN_FLIP_MENU_HEIGHT = 80;
const MIN_MENU_LIST_HEIGHT = 80;
const FALLBACK_MENU_SEARCH_HEIGHT = 52;

/** Convert viewport coords to the element that actually contains `position:fixed` (transformed dialogs). */
function toPositionCoords(menu, viewportX, viewportY) {
  let node = menu.parentElement;
  while (node && node !== document.documentElement) {
    const style = window.getComputedStyle(node);
    const transformed = style.transform && style.transform !== 'none';
    const filtered = style.filter && style.filter !== 'none';
    if (transformed || filtered || style.willChange?.includes('transform')) {
      const parentRect = node.getBoundingClientRect();
      return {
        x: viewportX - parentRect.left,
        y: viewportY - parentRect.top,
        height: parentRect.height,
      };
    }
    node = node.parentElement;
  }
  return {
    x: viewportX,
    y: viewportY,
    height: window.innerHeight || document.documentElement.clientHeight,
  };
}

function applyMenuListHeight(menu, cappedHeight) {
  const searchEl = menu.querySelector(
    '.tag-combobox__menu-search, .lb-dropdown__menu-search, .user-chip-multi-select__add-task-search',
  );
  const footerEl = menu.querySelector('.user-chip-multi-select__add-task-footer');
  const searchHeight = searchEl?.offsetHeight || FALLBACK_MENU_SEARCH_HEIGHT;
  const footerHeight = footerEl?.offsetHeight || 0;
  const listMaxHeight = Math.max(MIN_MENU_LIST_HEIGHT, cappedHeight - searchHeight - footerHeight - 16);

  menu.style.maxHeight = `${cappedHeight}px`;
  menu.style.height = 'auto';
  menu.style.minHeight = '0';
  menu.style.overflow = 'hidden';

  menu.querySelectorAll('.v-list, .user-chip-multi-select__add-task-list').forEach((el) => {
    el.style.minHeight = '0';
    el.style.maxHeight = `${listMaxHeight}px`;
    el.style.overflowY = 'auto';
  });
}

function isMenuContentEl(el) {
  return !!el && el.nodeType === 1 && el.classList?.contains('v-menu__content');
}

/** Resolve the open menu content element from a field ref or content class. */
export function resolveMenuElement({ menuClass, fieldRef } = {}) {
  // v-select/autocomplete: $refs.menu; v-menu itself: $refs.content
  const menuComponent = fieldRef?.$refs?.menu || fieldRef;
  if (menuComponent?.$refs) {
    const contentRef = menuComponent.$refs.content;
    const fromRef =
      (contentRef && contentRef.nodeType === 1 ? contentRef : null) ||
      contentRef?.$el ||
      (menuComponent.isActive && menuComponent.$el?.querySelector?.('.v-menu__content'));
    if (isMenuContentEl(fromRef)) return fromRef;
  }

  if (!menuClass) return null;

  const className = menuClass.split(/\s+/)[0];
  return (
    document.querySelector(`.${className}.v-menu__content.menuable__content__active`) ||
    document.querySelector(`.${className}.v-menu__content`)
  );
}

/** Clear stale manual position overrides so Vuetify can re-anchor the menu. */
export function clearMenuPositionOverrides(menuClass, fieldRef) {
  const menu = resolveMenuElement({ menuClass, fieldRef });
  if (!menu) return;

  ['top', 'bottom', 'left', 'right', 'transform', 'position'].forEach((prop) => {
    menu.style.removeProperty(prop);
  });
}

/**
 * Sync menu width, z-index, and list scroll — positioning is handled by Vuetify.
 */
export function syncAttachedMenuLayout({
  activatorEl,
  menuClass,
  fieldRef,
  maxMenuHeight = DEFAULT_MAX_MENU_HEIGHT,
}) {
  if (!activatorEl || !menuClass) return;

  const menu = resolveMenuElement({ menuClass, fieldRef });
  if (!menu) return;

  const rect = activatorEl.getBoundingClientRect();
  const width = rect.width;

  if (width) {
    menu.style.minWidth = `${width}px`;
    menu.style.width = `${width}px`;
    menu.style.maxWidth = `${width}px`;
  }

  menu.style.zIndex = String(DIALOG_MENU_Z_INDEX);
  menu.style.maxHeight = `${maxMenuHeight}px`;

  const searchEl = menu.querySelector(
    '.tag-combobox__menu-search, .lb-dropdown__menu-search, .user-chip-multi-select__add-task-search',
  );
  const footerEl = menu.querySelector('.user-chip-multi-select__add-task-footer');
  const searchHeight = searchEl?.offsetHeight || FALLBACK_MENU_SEARCH_HEIGHT;
  const footerHeight = footerEl?.offsetHeight || 0;
  const listMaxHeight = Math.max(MIN_MENU_LIST_HEIGHT, maxMenuHeight - searchHeight - footerHeight - 16);

  menu.querySelectorAll('.v-list, .user-chip-multi-select__add-task-list').forEach((el) => {
    el.style.minHeight = `${Math.min(MIN_MENU_LIST_HEIGHT, listMaxHeight)}px`;
    el.style.maxHeight = `${listMaxHeight}px`;
    el.style.overflowY = 'auto';
  });
}

/** Ask Vuetify to re-measure the open menu against its activator. */
export function updateOpenMenuDimensions(fieldRef) {
  const menu = fieldRef?.$refs?.menu;
  if (menu?.isActive && typeof menu.updateDimensions === 'function') {
    menu.updateDimensions();
  }
}

/**
 * Pin the menu above or below the field from available viewport space.
 * Uses containing-block coords so `position:fixed` still works inside transformed dialogs.
 */
export function positionMenuNearActivator({
  activatorEl,
  menuClass,
  fieldRef,
  maxMenuHeight = DEFAULT_MAX_MENU_HEIGHT,
  prefer = 'auto',
}) {
  if (!activatorEl || !menuClass) return;

  const menu = resolveMenuElement({ menuClass, fieldRef });
  if (!menu) return;

  const rect = activatorEl.getBoundingClientRect();
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
  const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
  const spaceAbove = Math.max(0, rect.top - MENU_GAP);
  const spaceBelow = Math.max(0, viewportHeight - rect.bottom - MENU_GAP);

  const openBelow = prefer === 'below'
    || (prefer !== 'above' && (
      spaceBelow >= MIN_FLIP_MENU_HEIGHT && spaceBelow >= spaceAbove
    ));

  const available = openBelow ? spaceBelow : spaceAbove;
  const cappedHeight = Math.min(
    maxMenuHeight,
    Math.max(MIN_FLIP_MENU_HEIGHT, available),
  );

  applyMenuListHeight(menu, cappedHeight);

  let left = rect.left;
  const menuWidth = rect.width || menu.offsetWidth;
  if (menuWidth && left + menuWidth > viewportWidth - MENU_GAP) {
    left = Math.max(MENU_GAP, viewportWidth - menuWidth - MENU_GAP);
  }

  if (rect.width) {
    menu.style.minWidth = `${rect.width}px`;
    menu.style.width = `${rect.width}px`;
    menu.style.maxWidth = `${rect.width}px`;
  }

  const positionHeight = Math.min(cappedHeight, menu.offsetHeight || cappedHeight);
  const viewportTop = openBelow
    ? rect.bottom + MENU_GAP
    : rect.top - MENU_GAP - positionHeight;
  const local = toPositionCoords(menu, left, viewportTop);

  menu.style.zIndex = String(DIALOG_MENU_Z_INDEX);
  menu.style.setProperty('position', 'fixed', 'important');
  menu.style.setProperty('top', `${local.y}px`, 'important');
  menu.style.setProperty('bottom', 'auto', 'important');
  menu.style.setProperty('left', `${local.x}px`, 'important');
  menu.style.setProperty('right', 'auto', 'important');
  menu.style.setProperty('transform', 'none', 'important');
}

/** Pin menu above the field. Prefer `positionMenuNearActivator` for auto-flip. */
export function positionMenuAboveActivator(opts = {}) {
  return positionMenuNearActivator({ ...opts, prefer: 'above' });
}
